import { useTranslation } from 'react-i18next';
import { Button, RevealOnView } from '@/components/ui';
import { BUTTON_SLIDE, CONTENT_SLIDE } from '@/utils/animation';

/**
 * Patient care information section.
 *
 * Figma (node 38:10361, large 1440px):
 *   - bg `#fafafa` (clearlab-oat — page bg, so the section is left transparent)
 *   - `flex flex-col gap-[120px] items-start px-[120px] py-[200px]`
 *   - Children:
 *     1. Header row (2-col `flex items-start justify-between`):
 *        - Title: Karla Regular 32/32 tracking -0.025em, color #3E3A36 (dusk), w-[440px]
 *        - Right col: `flex-col gap-[56px] w-[600px]` — paragraph (16/44 dusk) +
 *          outlined blue chevron button "View care information"
 *     2. Disclaimer row (`flex flex-col gap-[24px] w-full`):
 *        - 0.7px hairline (border-clearlab-divider-darker)
 *        - Disclaimer paragraph: Karla Regular 12/14 #62605E (steel), full width
 *
 * Responsive:
 *   Mobile        : 1-col stack
 *   Tablet/Small  : 2-col w-60 title + flex-1 right
 *   XL+           : 2-col w-[440px] title + w-[600px] right, justify-between
 *
 * Font specs are constant across breakpoints (title 32/32, body 16/44, disclaimer 12/14).
 */
export function PatientCareSection() {
  const { t } = useTranslation();
  return (
    <section
      aria-labelledby="patient-care-heading"
      className="px-6 py-30 md:px-14 lg:px-20 lg:py-50 xl:px-30"
    >
      <RevealOnView
        {...CONTENT_SLIDE}
        className="mx-auto flex w-full max-w-content-xl flex-col gap-20 lg:gap-30"
      >
        <div className="flex w-full flex-col gap-10 md:flex-row md:items-start md:gap-10 xl:justify-between xl:gap-10">
          <h2
            id="patient-care-heading"
            className="text-[32px] font-normal leading-8 tracking-[-0.025em] text-clearlab-dusk md:w-60 md:flex-none xl:w-[440px]"
          >
            {t('products.patientCare.title')}
          </h2>

          <div className="flex flex-col items-start gap-14 md:flex-1 xl:w-[600px] xl:flex-none">
            <p className="text-[16px] font-normal leading-[44px] text-clearlab-midnight">
              {t('products.patientCare.body')}
            </p>
            <RevealOnView {...BUTTON_SLIDE}>
              <Button
                to="/patient-care"
                variant="outlined"
                size="small"
                tone="blue"
                iconRight
                className="px-4 py-2"
              >
                {t('products.patientCare.cta')}
              </Button>
            </RevealOnView>
          </div>
        </div>

        <div className="flex w-full flex-col items-start gap-6">
          <hr className="w-full border-0 border-t-[0.7px] border-clearlab-divider-darker" />
          <p className="w-full text-[12px] font-normal leading-[14px] text-clearlab-steel">
            {t('products.disclaimer')}
          </p>
        </div>
      </RevealOnView>
    </section>
  );
}
